import { Box, Flex, IconButton, Text, Textarea } from '@chakra-ui/react';
import { FiSend } from '@react-icons/all-files/fi/FiSend';
import { KeyboardEvent, useState } from 'react';

const MAX_LENGTH = 1000;
const COUNTER_VISIBLE_WITHIN = 150;

type MessageComposerProps = {
  onSend: (body: string) => Promise<void>;
  placeholder?: string;
};

const MessageComposer = ({ onSend, placeholder = 'Message the buyer…' }: MessageComposerProps) => {
  const [text, setText] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [failed, setFailed] = useState(false);

  const canSend = text.trim() !== '' && !isSending;
  const remaining = MAX_LENGTH - text.length;

  const handleSend = async () => {
    if (!canSend) return;
    setIsSending(true);
    setFailed(false);
    try {
      await onSend(text);
      setText('');
    } catch {
      setFailed(true);
    } finally {
      setIsSending(false);
    }
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      handleSend();
    }
  };

  return (
    <Box borderTopWidth="1px" borderTopColor="grayMediumBackground" px="16px" py="12px">
      {failed && (
        <Text fontSize="12px" color="leaseEndRed" mb="6px">
          Not sent. Try again.
        </Text>
      )}
      <Flex align="flex-end" gap="10px">
        <Textarea
          aria-label={placeholder}
          value={text}
          onChange={(event) => setText(event.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          maxLength={MAX_LENGTH}
          rows={1}
          resize="none"
          fontSize="14px"
          borderRadius="12px"
          borderColor="gainsboro"
          _focusVisible={{ borderColor: 'leaseEndBlue', boxShadow: 'none' }}
        />
        <IconButton
          aria-label="Send message"
          icon={<FiSend />}
          onClick={handleSend}
          isDisabled={!canSend}
          isLoading={isSending}
          bg="leaseEndBlue"
          color="white"
          borderRadius="full"
          fontSize="16px"
          minW="40px"
          h="40px"
          flexShrink={0}
          _hover={{ bg: 'darkMidnightBlue' }}
        />
      </Flex>
      {remaining <= COUNTER_VISIBLE_WITHIN && (
        <Text mt="6px" fontSize="11px" color="grayMedium" textAlign="right">
          {remaining} left
        </Text>
      )}
    </Box>
  );
};

export default MessageComposer;
